import React, { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Loader2, RotateCcw, Save, Check, Square, ChevronUp, ChevronDown, Plus, Copy, Trash2, Edit3, Power } from "lucide-react";
import { toast } from "sonner";

/**
 * Editor "Ambulatori e moduli" — gestione completa dei modelli cartella per medico:
 * - 12 preset di sistema + ambulatori custom illimitati
 * - Crea / Duplica / Rinomina / Elimina / Attiva-Disattiva / Riordina
 * - Per ogni ambulatorio: scegli blocchi clinici + diagnostica predefinita
 */
export default function ClinicTemplatesEditor() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null); // {library, templates, ordered}
  const [activeKey, setActiveKey] = useState("generale");
  const [working, setWorking] = useState({ blocks: [], diagnostica_default: [] });
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newAmbLabel, setNewAmbLabel] = useState("");
  const [newAmbDupFrom, setNewAmbDupFrom] = useState("generale");
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/medico/clinic-templates");
      setData(data);
      const first = data.templates[activeKey] || data.templates.generale;
      setWorking({ blocks: [...(first.blocks || [])], diagnostica_default: [...(first.diagnostica_default || [])] });
    } catch (e) { toast.error(apiErr(e, "Errore caricamento modelli")); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!data) return;
    const t = data.templates[activeKey];
    if (t) setWorking({ blocks: [...(t.blocks || [])], diagnostica_default: [...(t.diagnostica_default || [])] });
  }, [activeKey, data]);

  const lib = useMemo(() => data?.library || [], [data]);
  const byKey = useMemo(() => Object.fromEntries(lib.map((b) => [b.key, b])), [lib]);

  const isDiagnostica = (k) => byKey[k]?.group === "diagnostica";

  const toggle = (key) => {
    const target = isDiagnostica(key) ? "diagnostica_default" : "blocks";
    setWorking((w) => {
      const cur = new Set(w[target]);
      if (cur.has(key)) cur.delete(key); else cur.add(key);
      // Ordinamento: preserva ordine libreria
      const ordered = lib.filter((b) => cur.has(b.key)).map((b) => b.key);
      return { ...w, [target]: ordered };
    });
  };

  const move = (key, dir) => {
    const target = isDiagnostica(key) ? "diagnostica_default" : "blocks";
    setWorking((w) => {
      const arr = [...w[target]];
      const idx = arr.indexOf(key);
      if (idx < 0) return w;
      const ni = idx + dir;
      if (ni < 0 || ni >= arr.length) return w;
      [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
      return { ...w, [target]: arr };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/medico/clinic-templates/${activeKey}`, working);
      toast.success("Modello salvato");
      await load();
    } catch (e) { toast.error(apiErr(e, "Errore salvataggio")); }
    finally { setSaving(false); }
  };

  const resetDefault = async () => {
    if (!window.confirm("Ripristinare il modello ai valori di default?")) return;
    setSaving(true);
    try {
      await api.post(`/medico/clinic-templates/${activeKey}/reset`);
      toast.success("Modello ripristinato");
      await load();
    } catch (e) { toast.error(apiErr(e, "Errore reset")); }
    finally { setSaving(false); }
  };

  const createCustom = async () => {
    if (!newAmbLabel.trim()) { toast.error("Indica un nome ambulatorio"); return; }
    setSaving(true);
    try {
      const { data: created } = await api.post("/medico/clinic-templates", {
        label: newAmbLabel.trim(),
        duplicate_from: newAmbDupFrom || undefined,
      });
      toast.success(`Ambulatorio "${created.label}" creato`);
      setShowCreate(false);
      setNewAmbLabel("");
      await load();
      setActiveKey(created.key);
    } catch (e) { toast.error(apiErr(e, "Errore creazione")); }
    finally { setSaving(false); }
  };

  const duplicateCurrent = async () => {
    const src = data?.templates?.[activeKey];
    if (!src) return;
    setNewAmbLabel(`${src.label} (copia)`);
    setNewAmbDupFrom(activeKey);
    setShowCreate(true);
  };

  const deleteCurrent = async () => {
    const t = data?.templates?.[activeKey];
    if (!t || !t.is_custom) { toast.error("I preset di sistema non si eliminano (puoi disabilitarli)"); return; }
    if (!window.confirm(`Eliminare definitivamente "${t.label}"?`)) return;
    setSaving(true);
    try {
      await api.delete(`/medico/clinic-templates/${activeKey}`);
      toast.success("Ambulatorio eliminato");
      setActiveKey("generale");
      await load();
    } catch (e) { toast.error(apiErr(e, "Errore eliminazione")); }
    finally { setSaving(false); }
  };

  const renameActive = async () => {
    if (!renameValue.trim()) { setRenameOpen(false); return; }
    setSaving(true);
    try {
      await api.put(`/medico/clinic-templates/${activeKey}`, { label: renameValue.trim() });
      toast.success("Rinominato");
      setRenameOpen(false);
      await load();
    } catch (e) { toast.error(apiErr(e, "Errore rinomina")); }
    finally { setSaving(false); }
  };

  const toggleEnabled = async () => {
    const t = data?.templates?.[activeKey];
    if (!t) return;
    try {
      await api.put(`/medico/clinic-templates/${activeKey}`, { enabled: !t.enabled });
      toast.success(t.enabled ? "Disattivato" : "Attivato");
      await load();
    } catch (e) { toast.error(apiErr(e, "Errore")); }
  };

  const reorderTab = async (key, dir) => {
    const ordered = data?.ordered || [];
    const idx = ordered.findIndex((t) => t.key === key);
    if (idx < 0) return;
    const ni = idx + dir;
    if (ni < 0 || ni >= ordered.length) return;
    const newOrder = [...ordered];
    [newOrder[idx], newOrder[ni]] = [newOrder[ni], newOrder[idx]];
    const payload = newOrder.map((t, i) => ({ key: t.key, order: i }));
    try {
      await api.post("/medico/clinic-templates/reorder", payload);
      await load();
    } catch (e) { toast.error(apiErr(e, "Errore riordino")); }
  };

  if (loading || !data) return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] p-8 text-center text-slate-500">
      <Loader2 className="animate-spin mx-auto mb-2" /> Caricamento modelli…
    </div>
  );

  // Raggruppa blocchi della libreria per gruppo per UI più leggibile
  // Fase 31: il gruppo "anamnesi" è escluso dai togglable — l'anamnesi è dato master
  // paziente e sempre presente in cartella.
  const groups = ["visus", "refrazione", "tono", "segmento_anteriore", "segmento_posteriore", "motilita", "diagnostica", "prestazioni", "terapia", "ai", "chiusura"];
  const groupLabel = {
    visus: "Visus",
    refrazione: "Refrazione",
    tono: "Tono / IOP",
    segmento_anteriore: "Segmento anteriore",
    segmento_posteriore: "Segmento posteriore / Fundus",
    motilita: "Motilità / Ortottica",
    diagnostica: "Diagnostica strumentale",
    prestazioni: "Prestazioni autonome (Laser, Hess, ecc.)",
    terapia: "Terapia / Prescrizione",
    ai: "IA / Diagnosi / Protocollo",
    chiusura: "Chiusura / Referto",
  };

  const isSelected = (k) => isDiagnostica(k) ? working.diagnostica_default.includes(k) : working.blocks.includes(k);
  const orderIndex = (k) => {
    const target = isDiagnostica(k) ? working.diagnostica_default : working.blocks;
    return target.indexOf(k);
  };

  return (
    <div className="space-y-4" data-testid="clinic-templates-editor">
      <div className="bg-brand-azure/5 border border-brand-azure/30 rounded-xl p-4 text-xs text-brand-navy flex items-start justify-between gap-3">
        <div>
          Configura i tuoi <b>ambulatori</b> in modo modulare. Crea ambulatori custom, duplica, rinomina, riordina, attiva/disattiva. Le modifiche sono <b>personali</b> per il tuo account.
        </div>
        <button
          onClick={() => { setShowCreate(true); setNewAmbDupFrom(activeKey); setNewAmbLabel(""); }}
          data-testid="amb-new"
          className="shrink-0 inline-flex items-center gap-1.5 bg-brand-azure hover:bg-brand-navyDark text-white font-bold text-xs px-3 py-2 rounded-lg shadow-sm"
        >
          <Plus size={13} /> Nuovo ambulatorio
        </button>
      </div>

      {/* Tab ambulatori (ordinati) */}
      <div className="flex flex-wrap gap-1.5" data-testid="clinic-templates-tabs">
        {(data.ordered || Object.values(data.templates)).map((t, idx, arr) => (
          <div key={t.key} className="flex items-center">
            <button
              onClick={() => setActiveKey(t.key)}
              data-testid={`tpl-tab-${t.key}`}
              className={`px-3 py-1.5 rounded-lg font-bold text-xs transition flex items-center gap-1.5 ${activeKey === t.key ? "bg-brand-navy text-white" : "bg-white border border-[#E2E8F0] text-slate-600 hover:bg-slate-100"} ${!t.enabled ? "opacity-50 line-through" : ""}`}
            >
              {t.label}
              {t.is_custom && <span className="text-[9px] uppercase bg-amber-200 text-amber-900 px-1 rounded">custom</span>}
              {t.is_customized && !t.is_custom && <span className="text-amber-400">●</span>}
            </button>
            {activeKey === t.key && (
              <div className="flex items-center gap-0.5 ml-1">
                <button onClick={() => reorderTab(t.key, -1)} disabled={idx === 0} className="p-1 text-slate-400 hover:text-brand-navy disabled:opacity-30" title="Sposta su" data-testid={`amb-up-${t.key}`}><ChevronUp size={13} /></button>
                <button onClick={() => reorderTab(t.key, 1)} disabled={idx === arr.length - 1} className="p-1 text-slate-400 hover:text-brand-navy disabled:opacity-30" title="Sposta giù" data-testid={`amb-down-${t.key}`}><ChevronDown size={13} /></button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Action bar per ambulatorio attivo */}
      <div className="flex flex-wrap items-center gap-2 bg-slate-50 border border-[#E2E8F0] rounded-lg p-2" data-testid="amb-actions">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Azioni:</span>
        <button onClick={() => { setRenameOpen(true); setRenameValue(data.templates[activeKey]?.label || ""); }} className="inline-flex items-center gap-1 text-xs font-bold text-slate-700 hover:text-brand-navy px-2 py-1 rounded hover:bg-white" data-testid="amb-rename">
          <Edit3 size={12} /> Rinomina
        </button>
        <button onClick={duplicateCurrent} className="inline-flex items-center gap-1 text-xs font-bold text-slate-700 hover:text-brand-navy px-2 py-1 rounded hover:bg-white" data-testid="amb-duplicate">
          <Copy size={12} /> Duplica
        </button>
        <button onClick={toggleEnabled} className="inline-flex items-center gap-1 text-xs font-bold text-slate-700 hover:text-brand-navy px-2 py-1 rounded hover:bg-white" data-testid="amb-toggle-enabled">
          <Power size={12} /> {data.templates[activeKey]?.enabled === false ? "Attiva" : "Disattiva"}
        </button>
        {data.templates[activeKey]?.is_custom && (
          <button onClick={deleteCurrent} className="inline-flex items-center gap-1 text-xs font-bold text-red-600 hover:text-red-800 px-2 py-1 rounded hover:bg-white" data-testid="amb-delete">
            <Trash2 size={12} /> Elimina
          </button>
        )}
        <div className="ml-auto text-[10px] text-slate-500">
          {data.templates[activeKey]?.is_custom ? "Ambulatorio personalizzato" : "Preset di sistema"}
        </div>
      </div>

      {/* Rename inline */}
      {renameOpen && (
        <div className="bg-white border border-brand-azure rounded-lg p-3 flex items-center gap-2" data-testid="amb-rename-bar">
          <input
            type="text"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            placeholder="Nuovo nome ambulatorio"
            className="flex-1 px-2 py-1.5 rounded border border-[#E2E8F0] text-sm focus:outline-none focus:border-brand-azure"
            data-testid="amb-rename-input"
            autoFocus
          />
          <button onClick={renameActive} className="bg-brand-navy text-white font-bold text-xs px-3 py-1.5 rounded" data-testid="amb-rename-save">Salva</button>
          <button onClick={() => setRenameOpen(false)} className="text-xs font-bold text-slate-500 px-2 py-1.5">Annulla</button>
        </div>
      )}

      {/* Modal Crea ambulatorio */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="amb-create-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 space-y-3">
            <h3 className="font-extrabold text-brand-navy text-lg">Nuovo ambulatorio</h3>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Nome</label>
              <input type="text" value={newAmbLabel} onChange={(e) => setNewAmbLabel(e.target.value)} placeholder="es. Ambulatorio Dry Eye" className="w-full mt-1 px-3 py-2 rounded border border-[#E2E8F0] focus:outline-none focus:border-brand-azure text-sm" data-testid="amb-new-label" autoFocus />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Duplica da</label>
              <select value={newAmbDupFrom} onChange={(e) => setNewAmbDupFrom(e.target.value)} className="w-full mt-1 px-3 py-2 rounded border border-[#E2E8F0] focus:outline-none focus:border-brand-azure text-sm bg-white" data-testid="amb-new-source">
                <option value="">Vuoto (nessun blocco)</option>
                {(data.ordered || []).map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowCreate(false)} className="text-xs font-bold text-slate-600 px-3 py-2">Annulla</button>
              <button onClick={createCustom} disabled={saving} className="bg-brand-navy text-white font-bold text-xs px-4 py-2 rounded disabled:opacity-60" data-testid="amb-new-confirm">Crea</button>
            </div>
          </div>
        </div>
      )}

      {/* Body: 2 colonne — sinistra libreria per gruppi, destra ordine selezione */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Libreria selezionabile */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 max-h-[60vh] overflow-y-auto">
          <h4 className="font-extrabold text-brand-navy text-sm mb-2">Libreria blocchi</h4>
          <div className="bg-sky-50 border border-sky-200 rounded-lg px-3 py-2 mb-3 text-[11px] text-sky-800 flex items-start gap-2" data-testid="tpl-anamnesi-note">
            <span className="mt-0.5">ℹ️</span>
            <span><b>Anamnesi paziente:</b> sempre presente in cartella (dato master). Non è selezionabile per singolo modulo.</span>
          </div>
          {groups.map((g) => {
            const items = lib.filter((b) => b.group === g);
            if (items.length === 0) return null;
            return (
              <div key={g} className="mb-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">{groupLabel[g] || g}</div>
                <ul className="space-y-1">
                  {items.map((b) => (
                    <li key={b.key}>
                      <label className="flex items-start gap-2 cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded" data-testid={`tpl-block-${b.key}`}>
                        <input
                          type="checkbox"
                          checked={isSelected(b.key)}
                          onChange={() => toggle(b.key)}
                          className="mt-0.5"
                        />
                        <span className="text-xs text-slate-700 flex-1">
                          {b.label}
                          {isDiagnostica(b.key) && <span className="ml-1 text-[9px] text-brand-azure font-bold">DIAG</span>}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Selezione attuale ordinata */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 max-h-[60vh] overflow-y-auto">
          <h4 className="font-extrabold text-brand-navy text-sm mb-2">Sezioni nel modello (in ordine)</h4>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-1 mb-1">Blocchi clinici</div>
          {working.blocks.length === 0 && <div className="text-xs text-slate-400 italic">Nessun blocco</div>}
          <ol className="space-y-1" data-testid="tpl-selected-blocks">
            {working.blocks.map((k, i) => (
              <li key={k} className="flex items-center gap-2 bg-slate-50 rounded px-2 py-1" data-testid={`tpl-selected-${k}`}>
                <span className="text-[10px] text-slate-400 w-5">{i + 1}.</span>
                <span className="text-xs text-brand-navy flex-1">{byKey[k]?.label || k}</span>
                <button onClick={() => move(k, -1)} className="text-slate-400 hover:text-brand-navy p-0.5" data-testid={`tpl-up-${k}`}><ChevronUp size={12} /></button>
                <button onClick={() => move(k, 1)} className="text-slate-400 hover:text-brand-navy p-0.5" data-testid={`tpl-down-${k}`}><ChevronDown size={12} /></button>
                <button onClick={() => toggle(k)} className="text-red-400 hover:text-red-600 p-0.5"><Square size={12} /></button>
              </li>
            ))}
          </ol>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-4 mb-1">Diagnostica predefinita</div>
          {working.diagnostica_default.length === 0 && <div className="text-xs text-slate-400 italic">Nessuna diagnostica predefinita</div>}
          <ol className="space-y-1" data-testid="tpl-selected-diag">
            {working.diagnostica_default.map((k, i) => (
              <li key={k} className="flex items-center gap-2 bg-blue-50 rounded px-2 py-1">
                <span className="text-[10px] text-slate-400 w-5">{i + 1}.</span>
                <span className="text-xs text-brand-navy flex-1">{byKey[k]?.label || k}</span>
                <button onClick={() => move(k, -1)} className="text-slate-400 hover:text-brand-navy p-0.5"><ChevronUp size={12} /></button>
                <button onClick={() => move(k, 1)} className="text-slate-400 hover:text-brand-navy p-0.5"><ChevronDown size={12} /></button>
                <button onClick={() => toggle(k)} className="text-red-400 hover:text-red-600 p-0.5"><Square size={12} /></button>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* Footer azioni */}
      <div className="sticky bottom-0 bg-white border-t border-[#E2E8F0] -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap justify-end gap-2">
        <button onClick={resetDefault} disabled={saving} className="text-sm font-bold text-slate-600 hover:text-red-600 px-3 py-2 flex items-center gap-1.5" data-testid="tpl-reset">
          <RotateCcw size={13} /> Ripristina default
        </button>
        <button onClick={save} disabled={saving} className="bg-brand-navy hover:bg-brand-navyDark text-white font-bold px-5 py-2 rounded-lg flex items-center gap-2 disabled:opacity-60" data-testid="tpl-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Salva modello
        </button>
      </div>
    </div>
  );
}
